import React, { useCallback } from 'react';
import { TouchableOpacity, View } from 'react-native';
import {
  Button,
  Checkbox,
  Input,
  KeyboardScreenContainer,
  Text,
} from '~/components';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { firstStepSchema } from '../../schemas';
import Footer from '../Footer';
import Banner from '~/scenes/MarisaTakesCare/components/Banner';
import type { StepComponentProps } from '~/components/StepsForm';
import { useTakesCareFormStore } from '~/stores/marisaTakesCareForm';
import type { FirstStepFormData } from '~/@types/MarisaTakesCare/form';
import { GENERAL_CONDITIONS_LINK } from '~/scenes/MarisaTakesCare/constants';
import { MARISA_TAKES_CARE_ACCESSION_TERMS_LINK } from '~/utils/url';
import { isWeb } from '~/utils/device';
import { MarisaAppUtils } from '~/utils/MarisaAppUtils';
import { useSalesFunnel } from '~/stores';

const FirstStep: React.FC<StepComponentProps> = ({ onContinue }) => {
  const {
    functions: { setFirstStepData },
  } = useTakesCareFormStore();
  const { setInfos } = useSalesFunnel();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FirstStepFormData>({
    resolver: yupResolver(firstStepSchema),
  });

  const onSubmit = useCallback(
    (data: FirstStepFormData) => {
      setFirstStepData(data);
      setInfos({
        fullName: data.fullName,
        email: data.email,
        cellphone: data.cellphone,
      });
      onContinue?.();
    },
    [onContinue, setFirstStepData, setInfos]
  );

  return (
    <KeyboardScreenContainer
      footer={
        <View className="flex xl:hidden">
          <Footer onSubmit={handleSubmit(onSubmit)} />
        </View>
      }
    >
      <View className="flex-1 container mx-auto xl:flex-row xl:pt-12 xl:pb-24">
        <Banner showButton={false} />

        <View className="mt-8 xl:mt-0 px-6 flex-1 xl:ml-24">
          <Text className="text-xl mb-4 text-gray-600">
            Informações pessoais
          </Text>

          <Text className="text-gray-400 text-sm mb-8">
            Para seguirmos com a contratação, precisamos de algumas informações
            do segurado.
          </Text>

          <Input
            control={control}
            label="Nome completo"
            name="fullName"
            placeholder="Nome e Sobrenome"
            keyboardType="ascii-capable"
            error={errors.fullName?.message}
            maxLength={100}
          />

          <Input
            control={control}
            label="Email"
            name="email"
            placeholder="Insira o seu email"
            keyboardType="email-address"
            error={errors.email?.message}
            autoCapitalize="none"
            maxLength={100}
          />

          <Input
            control={control}
            label="Celular"
            name="cellphone"
            placeholder="DDD+Celular"
            keyboardType="number-pad"
            error={errors.cellphone?.message}
            maskConfig={{ type: 'cel-phone' }}
          />

          <Checkbox
            control={control}
            name="acceptGeneralConditions"
            className="mt-6"
            error={errors.acceptGeneralConditions?.message}
          >
            <View className="flex-row items-center">
              <Text className="text-xs text-gray-600">Eu concordo com as </Text>
              <TouchableOpacity
                activeOpacity={0.6}
                onPress={() => MarisaAppUtils.openUrl(GENERAL_CONDITIONS_LINK)}
              >
                <Text className="text-primary-600 font-medium text-xs">
                  Condições Gerais
                </Text>
              </TouchableOpacity>
            </View>
          </Checkbox>

          <Checkbox
            control={control}
            name="acceptAccessionTerms"
            className="my-4"
            error={errors.acceptAccessionTerms?.message}
          >
            <View className="flex-row items-center">
              <Text className="text-xs text-gray-600">Eu concordo com os </Text>
              <TouchableOpacity
                activeOpacity={0.6}
                onPress={() =>
                  MarisaAppUtils.openUrl(MARISA_TAKES_CARE_ACCESSION_TERMS_LINK)
                }
              >
                <Text className="text-primary-600 font-medium text-xs">
                  Termos de Adesão
                </Text>
              </TouchableOpacity>
            </View>
          </Checkbox>

          {isWeb && (
            <View className="flex-1 justify-end hidden xl:flex">
              <Button className="w-ful" onPress={handleSubmit(onSubmit)}>
                CONTINUAR
              </Button>
            </View>
          )}
        </View>
      </View>
    </KeyboardScreenContainer>
  );
};

export default FirstStep;
